// import { acceptedService, run, show } from 'vanilla-cookieconsent';
import * as CookieConsent from 'vanilla-cookieconsent';


// Cookie Consent Categories
export const CAT_NECESSARY = "necessary" as const;
export const CAT_ANALYTICS = "analytics" as const;
export const CAT_ADVERTISEMENT = "advertisement" as const;
export const CAT_FUNCTIONALITY = "functionality" as const;
export const CAT_SECURITY = "security" as const;

// Google Tag Manager Services
export const SERVICE_AD_STORAGE = 'ad_storage' as const;
export const SERVICE_AD_USER_DATA = 'ad_user_data' as const;
export const SERVICE_AD_PERSONALIZATION = 'ad_personalization' as const;
export const SERVICE_ANALYTICS_STORAGE = 'analytics_storage' as const;
export const SERVICE_FUNCTIONALITY_STORAGE = 'functionality_storage' as const;
export const SERVICE_PERSONALIZATION_STORAGE = 'personalization_storage' as const;
export const SERVICE_SECURITY_STORAGE = 'security_storage' as const;

// Type definitions
// export type ConsentStatus = 'granted' | 'denied';

export type CategoryType = typeof CAT_NECESSARY | typeof CAT_ANALYTICS | typeof CAT_ADVERTISEMENT | typeof CAT_FUNCTIONALITY | typeof CAT_SECURITY;

export type ServiceType = typeof SERVICE_AD_STORAGE | typeof SERVICE_AD_USER_DATA | typeof SERVICE_AD_PERSONALIZATION | 
                         typeof SERVICE_ANALYTICS_STORAGE | typeof SERVICE_FUNCTIONALITY_STORAGE | 
                         typeof SERVICE_PERSONALIZATION_STORAGE | typeof SERVICE_SECURITY_STORAGE;


/**
 * Updates Google Tag Manager consent based on user choices
 * @param cookieConsent - CookieConsent library instance
 */
export function updateGtagConsent(): void {
    const consentParams: Gtag.ConsentParams = {
        [SERVICE_ANALYTICS_STORAGE]: CookieConsent.acceptedService(SERVICE_ANALYTICS_STORAGE, CAT_ANALYTICS) ? 'granted' : 'denied',
        [SERVICE_AD_STORAGE]: CookieConsent.acceptedService(SERVICE_AD_STORAGE, CAT_ADVERTISEMENT) ? 'granted' : 'denied',
        [SERVICE_AD_USER_DATA]: CookieConsent.acceptedService(SERVICE_AD_USER_DATA, CAT_ADVERTISEMENT) ? 'granted' : 'denied',
        [SERVICE_AD_PERSONALIZATION]: CookieConsent.acceptedService(SERVICE_AD_PERSONALIZATION, CAT_ADVERTISEMENT) ? 'granted' : 'denied',
        [SERVICE_FUNCTIONALITY_STORAGE]: CookieConsent.acceptedService(SERVICE_FUNCTIONALITY_STORAGE, CAT_FUNCTIONALITY) ? 'granted' : 'denied',
        [SERVICE_PERSONALIZATION_STORAGE]: CookieConsent.acceptedService(SERVICE_PERSONALIZATION_STORAGE, CAT_FUNCTIONALITY) ? 'granted' : 'denied',
        [SERVICE_SECURITY_STORAGE]: CookieConsent.acceptedService(SERVICE_SECURITY_STORAGE, CAT_SECURITY) ? 'granted' : 'denied',
    };

    gtag('consent', 'update', consentParams);
}

/**
 * Creates default cookie consent configuration
 * @param cookieConsent - CookieConsent library instance
 * @returns Default configuration object
 */
export function createDefaultConfig(): CookieConsent.CookieConsentConfig {
    return {
        guiOptions: {
            consentModal: {
                layout: "bar",
                position: "bottom",
                equalWeightButtons: false,
                flipButtons: false
            },
            preferencesModal: {
                layout: "box",
                position: "right",
                equalWeightButtons: true,
                flipButtons: false
            }
        },

        onFirstConsent: () => updateGtagConsent(),
        onConsent: () => updateGtagConsent(),
        onChange: () => updateGtagConsent(),

        categories: {
            [CAT_NECESSARY]: {
                enabled: true,
                readOnly: true,
            },
            [CAT_ANALYTICS]: {
                autoClear: {
                    cookies: [
                        { name: /^_ga/ },
                        { name: '_gid' }
                    ]
                },
                services: {
                    [SERVICE_ANALYTICS_STORAGE]: {
                        label: 'Enables storage (such as cookies) related to analytics e.g. visit duration.',
                    }
                }
            },
            [CAT_ADVERTISEMENT]: {
                services: {
                    [SERVICE_AD_STORAGE]: {
                        label: 'Enables storage (such as cookies) related to advertising.',
                    },
                    [SERVICE_AD_USER_DATA]: {
                        label: 'Sets consent for sending user data related to advertising to Google.',
                    },
                    [SERVICE_AD_PERSONALIZATION]: {
                        label: 'Sets consent for personalized advertising.',
                    },
                }
            },
            [CAT_FUNCTIONALITY]: {
                services: {
                    [SERVICE_FUNCTIONALITY_STORAGE]: {
                        label: 'Enables storage that supports the functionality of the website or app e.g. language settings.',
                    },
                    [SERVICE_PERSONALIZATION_STORAGE]: {
                        label: 'Enables storage related to personalization e.g. video recommendations.',
                    },
                }
            },
            [CAT_SECURITY]: {
                services: {
                    [SERVICE_SECURITY_STORAGE]: {
                        label: 'Enables storage related to security such as authentication functionality, fraud prevention, and other user protection.',
                    },
                }
            }
        },

        language: {
            default: 'en',
            translations: {
                en: {
                    consentModal: {
                        title: 'We use cookies',
                        description: 'This website uses essential cookies to ensure its proper operation and tracking cookies to understand how you interact with it. The latter will be set only after consent.',
                        acceptAllBtn: 'Accept all',
                        acceptNecessaryBtn: 'Reject all',
                        showPreferencesBtn: 'Manage Individual preferences'
                    },
                    preferencesModal: {
                        title: 'Manage cookie preferences',
                        acceptAllBtn: 'Accept all',
                        acceptNecessaryBtn: 'Reject all',
                        savePreferencesBtn: 'Accept current selection',
                        closeIconLabel: 'Close modal',
                        sections: [
                            {
                                title: "Cookie usage",
                                description: "We use cookies to ensure the basic functionalities of the website and to enhance your online experience."
                            },
                            {
                                title: "Strictly necessary cookies",
                                description: "These cookies are essential for the proper functioning of the website, for example for user authentication.",
                                linkedCategory: CAT_NECESSARY,
                            },
                            {
                                title: "Analytics",
                                description: 'Cookies used for analytics help collect data that allows services to understand how users interact with a particular service. These insights allow services both to improve content and to build better features that improve the user\'s experience.',
                                linkedCategory: CAT_ANALYTICS,
                                cookieTable: {
                                    headers: {
                                        name: "Name",
                                        domain: "Service",
                                        description: "Description",
                                        expiration: "Expiration"
                                    },
                                    body: [
                                        {
                                            name: "_ga",
                                            domain: "Google Analytics",
                                            description: "Cookie set by <a href=\"https://business.safety.google/adscookies/\">Google Analytics</a>",
                                            expiration: "Expires after 12 days"
                                        },
                                        {
                                            name: "_gid",
                                            domain: "Google Analytics",
                                            description: "Cookie set by <a href=\"https://business.safety.google/adscookies/\">Google Analytics</a>",
                                            expiration: "Session"
                                        }
                                    ]
                                }
                            },
                            {
                                title: 'Advertising',
                                description: 'Google uses cookies for advertising, including serving and rendering ads, personalizing ads (depending on your ad settings at <a href="https://g.co/adsettings">g.co/adsettings</a>), limiting the number of times an ad is shown to a user, muting ads you have chosen to stop seeing, and measuring the effectiveness of ads.',
                                linkedCategory: CAT_ADVERTISEMENT,
                            },
                            {
                                title: 'Functionality',
                                description: 'Cookies used for functionality allow users to interact with a service or site to access features that are fundamental to that service. Things considered fundamental to the service include preferences like the user\'s choice of language, product optimizations that help maintain and improve a service, and maintaining information relating to a user\'s session, such as the content of a shopping cart.',
                                linkedCategory: CAT_FUNCTIONALITY,
                            },
                            {
                                title: 'Security',
                                description: 'Cookies used for security authenticate users, prevent fraud, and protect users as they interact with a service.',
                                linkedCategory: CAT_SECURITY,
                            },
                            {
                                title: 'More information',
                                description: 'For any queries in relation to the policy on cookies and your choices, please <a href="https://www.example.com/contacts">contact us</a>.'
                            }
                        ]
                    }
                }
            }
        }
    };
}